import { forwardRef, type JSX } from 'react';
import { useDiveContext } from '../../../context';
import { DiNavItem } from '../../DiNav/DiNavItem/DiNavItem';
import {
  navItemCVA,
  navItemIconCVA,
  navItemLabelCVA,
} from './HeaderNavItem.cva';
import type { HeaderNavItemProps } from './HeaderNavItem.types';

/**
 * HeaderNavItem component to be used inside a `<HeaderNav />` component.
 * It uses {@link https://floating-ui.com/docs/composite Floating/UI CompositeItem}
 * which makes the item navigable by arrow keys outside of floating element context,
 * whenever it is implemented inside a `<Composite>`.
 *
 * @example
 *
 * ```tsx
 * <HeaderNavItem label="Home" icon={<HomeIcon />} />
 * <HeaderNavItem label="Home" icon={<HomeIcon />} isCurrent /> // Current page
 * <HeaderNavItem label="Home" icon={<HomeIcon />} href="/home" /> // Link
 * <HeaderNavItem label="Home" icon={<HomeIcon />} wrapper={<Link href="/home" />} /> // Custom wrapper
 * ```
 */
const HeaderNavItem = forwardRef<HTMLElement, HeaderNavItemProps>(
  function HeaderNavItem(
    { isCurrent, font = 'hind', ...props },
    ref,
  ): JSX.Element {
    const { device } = useDiveContext();
    return (
      <DiNavItem
        ref={ref}
        font={font}
        role="menuitem"
        {...props}
        {...(isCurrent && { 'aria-current': 'page' })}
        buttonClassName={navItemCVA({ device, current: isCurrent })}
        iconClassName={navItemIconCVA({ device, current: isCurrent })}
        labelClassName={navItemLabelCVA({ device, current: isCurrent })}
      />
    );
  },
);

export default HeaderNavItem;
